import React, { useState, useCallback } from 'react'
import { Stack, Grid } from '@mui/material'
import {
  DateTimeInput,
  required,
  TextInput,
  FormTab,
  BooleanInput,
  maxLength,
  useTranslate,
  TransformData,
} from 'react-admin'
import dayjs from 'dayjs'
import { MyRichTextInput } from '../../components/text-editor'
import { isAfterDate, isBeforeDate, isSameDate, isYoutubeLink } from '../../utils/validators'
import { TabbedMultiCardForm } from '../../components/tab'
import { ApproveAndPublishToolBar } from '../../components/form'
// import { NewsPublishCondSelectInput } from '../../components/domains/news/NewsPublishCondSelectInput'
// import { NewsTypeSelectInput } from '../../components/domains/news/NewsTypeSelectInput'
import ApplyToEnglishButton from '../../components/buttons/ApplyToEnglishButton'
// import { NewsDeepLinkInput } from '../../components/domains/news/NewsDeepLinkInput'
import { CreateBase } from '../../components/base'
import { COMMON_FIELDS, BLOGS_FIELDS } from '../../providers/localization/constant'
import { ImageInput } from '../../components/inputs'
// import { FilterTypeProjectAutocompleteArrayInput } from '../../components/form/FilterTypeProjectAutocompleteArrayInput'
import { ImageField } from '../../components/fields'
import { CoverInput } from '../../components/inputs/CoverInput'
import { ThumbnailInput } from '../../components/inputs/ThumbnailInput'
import sanitizeHtml from 'sanitize-html'
import trim from 'lodash/trim'
import unescape from 'lodash/unescape'

const RESOURCE_PREFIX = 'resources.blogs'

const PublishPeriodInputs: React.FC = () => {
  const defaultPublishDate = dayjs()
  const defaultExpiredDate = defaultPublishDate.add(2, 'day')

  return (
    <>
      <DateTimeInput
        source='start_date'
        label={`${COMMON_FIELDS}.date.published_on`}
        fullWidth
        defaultValue={defaultPublishDate}
        validate={[
          required(),
          isBeforeDate('end_date', 'The publish date must be in before the expired date.'),
        ]}
      />
      <DateTimeInput
        source='end_date'
        label={`${COMMON_FIELDS}.date.expired_on`}
        fullWidth
        defaultValue={defaultExpiredDate}
        validate={[
          required(),
          isAfterDate('start_date', 'The expired date must be in after the publish date.'),
          isSameDate(),
        ]}
      />
    </>
  )
}
const PersistForm: React.FC = () => {
  return (
    <Grid container spacing={2}>
      {/* <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <NewsTypeSelectInput />
          </Grid>
          <Grid item xs={6}>
            <NewsPublishCondSelectInput />
          </Grid>
        </Grid>
      </Grid> */}
      {/* <Grid item xs={12}>
        <Grid container spacing={2}>
          <FilterTypeProjectAutocompleteArrayInput
            source='property_ids'
            depField='publish_condition'
            expectedValue='s'
            label='Select Project'
          />
        </Grid>
      </Grid> */}
      <Grid item xs={12} order={{ xs: -1, sm: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <PublishPeriodInputs />
            </Stack>
          </Grid>
          {/* <Grid item sm={6}>
            <BooleanInput
              source='is_notification'
              label={`${BLOGS_FIELDS}.notification`}
              sx={{
                '& .MuiFormControlLabel-root': { marginRight: 'auto' },
              }}
            />
          </Grid> 
           <Grid item sm={6}>
            <BooleanInput
              source='is_direct_marketing'
              label={`${COMMON_FIELDS}.direct_marketing_consents`}
              sx={{
                '& .MuiFormControlLabel-root': { marginRight: 'auto' },
              }}
            />
          </Grid>  */}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source='youtube_link'
              label='Youtube Link'
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[isYoutubeLink()]}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='slug_th'
              label={`${COMMON_FIELDS}.slug_th.label`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[required()]}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='slug_en'
              label={`${COMMON_FIELDS}.slug_en.label`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[required()]}
            />
          </Grid>
          <Grid item xs={12}>
            <BooleanInput
              color='primary'
              source='is_highlight'
              label={`${RESOURCE_PREFIX}.highlight.title`}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <CoverInput source='cover' label={`${BLOGS_FIELDS}.cover`} accept='image/*'>
          <ImageField source='src' title='title' />
        </CoverInput>
      </Grid>
      <Grid item xs={12}>
        <ThumbnailInput
          source='thumbnail'
          label={`${BLOGS_FIELDS}.thumbnail_path`}
          accept='image/*'
        >
          <ImageField source='src' title='title' />
        </ThumbnailInput>
      </Grid>
      <Grid item xs={12}>
        <ImageInput
          source='images'
          label={`${BLOGS_FIELDS}.images`}
          accept='image/*'
          helperText={`${BLOGS_FIELDS}.images_helper`}
          multiple
          initCrop={false}
        ></ImageInput>
      </Grid>
      {/* <Grid item xs={12}>
        <MultipleFileInput source='files' accept='application/pdf' />
      </Grid> */}
    </Grid>
  )
}

const BlogCreate: React.FC = () => {
  const [editorContentEnKey, setEditorContentEnKey] = useState(0)
  const reloadTextEditor = (): void => {
    setEditorContentEnKey(editorContentEnKey + 1)
  }
  const translate = useTranslate()
  const transform: TransformData = useCallback((data) => {
    return {
      ...data,
      title_th: sanitizeHtml(unescape(data.title_th)),
      title_en: sanitizeHtml(unescape(data.title_en)),
      short_description_th: sanitizeHtml(unescape(data.short_description_th)),
      short_description_en: sanitizeHtml(unescape(data.short_description_en)),
      description_th: sanitizeHtml(unescape(data.description_th)),
      description_en: sanitizeHtml(unescape(data.description_en)),
      slug_th: trim(data.slug_th),
      slug_en: trim(data.slug_en),
    }
  }, [])
  return (
    <CreateBase
      title={`${RESOURCE_PREFIX}.create.title`}
      listAction={{
        hasBackButton: true,
      }}
      noCard
      encType='multipart/form-data'
      transform={transform}
    >
      <TabbedMultiCardForm
        childrenForms={[<PersistForm key='blog-info-form' />]}
        formProps={{
          toolbar: null,
          toolbarBottom: <ApproveAndPublishToolBar encType='multipart/form-data' />,
        }}
      >
        <FormTab label='TH'>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                source='title_th'
                label={`${BLOGS_FIELDS}.title_th`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    50,
                    `${translate(`${BLOGS_FIELDS}.title_th`)} ต้องมีความยาวไม่เกิน 50 ตัวอักษร`,
                  ),
                ]}
                // disabled={preventEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='short_description_th'
                label={`${BLOGS_FIELDS}.short_description_th`}
                InputProps={{
                  fullWidth: true,
                }}
                multiline
                rows={3}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    150,
                    `${translate(
                      `${BLOGS_FIELDS}.short_description_th`,
                    )} ต้องมีความยาวไม่เกิน 150 ตัวอักษร`,
                  ),
                ]}
                // disabled={preventEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <MyRichTextInput
                fullWidth
                label={`${BLOGS_FIELDS}.description_th`}
                source='description_th'
                validate={[required()]}
                // disabled={preventEdit}
              />
            </Grid>
            <ApplyToEnglishButton updateKeyEditor={reloadTextEditor} />
          </Grid>
        </FormTab>
        <FormTab label='EN'>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                source='title_en'
                label={`${BLOGS_FIELDS}.title_en`}
                InputProps={{
                  fullWidth: true,
                }}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    50,
                    `${translate(`${BLOGS_FIELDS}.title_en`)} ต้องมีความยาวไม่เกิน 50 ตัวอักษร`,
                  ),
                ]}
                // disabled={preventEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='short_description_en'
                label={`${BLOGS_FIELDS}.short_description_en`}
                InputProps={{
                  fullWidth: true,
                }}
                multiline
                rows={3}
                fullWidth
                validate={[
                  required(),
                  maxLength(
                    150,
                    `${translate(
                      `${BLOGS_FIELDS}.short_description_en`,
                    )} ต้องมีความยาวไม่เกิน 150 ตัวอักษร`,
                  ),
                ]}
                // disabled={preventEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <MyRichTextInput
                fullWidth
                label={`${BLOGS_FIELDS}.description_en`}
                source='description_en'
                validate={[required()]}
                // disabled={preventEdit}
              />
            </Grid>
          </Grid>
        </FormTab>
      </TabbedMultiCardForm>
    </CreateBase>
  )
}

export default BlogCreate
